import React from "react";
import PropTypes from "prop-types";
import { Card, PillRow, LabelMeta, EmptyState, theme } from "../components/ui";
import { getPostingSkills } from "Common/skillHelpers";
import {
  JobApplicationStatusEnum,
  JobApplicationStatusLabel,
  JobApplicationStatusText,
} from "Constants/Enums/JobApplicantStatusEnum";
import { EmploymentTypeEnumLabel } from "Constants/Enums/EmploymentTypeEnum";
import { WorkAuthorizationStatusEnumLabel } from "Constants/Enums/WorkAuthorizationStatusEnum";

// Ordered hiring pipeline (Reject is handled separately).
const PIPELINE = [
  JobApplicationStatusEnum.Applied,
  JobApplicationStatusEnum.ShortList,
  JobApplicationStatusEnum.TermsAccepted,
  JobApplicationStatusEnum.InterviewInProgress,
  JobApplicationStatusEnum.InterviewCompleted,
  JobApplicationStatusEnum.FeedbackAdded,
  JobApplicationStatusEnum.DocumentSend,
  JobApplicationStatusEnum.DocumentRecieved,
  JobApplicationStatusEnum.OnboardingDone,
  JobApplicationStatusEnum.Hired,
];

const pipelineStatusColor = (s) => {
  if (s === JobApplicationStatusEnum.Reject) return "#b13e3e";
  if (s === JobApplicationStatusEnum.Hired) return "#0f5a2a";
  return "#2563eb";
};

function OverviewTab({ applicantData }) {
  if (!applicantData) {
    return <EmptyState title="No data available" icon="📭" />;
  }

  const job = applicantData.jobPosting || {};

  return (
    <div style={{ padding: "1.5rem" }}>
      <ApplicationStatus status={applicantData.status} />

      {/* Per Sara (May-23): auto-fit collapses the 2-column layout to a
          single column on narrow viewports (mobile/tablet) so the right
          column doesn't get cramped. minmax(320px, 1fr) means: each column
          gets at least 320px before wrapping. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "1.5rem",
          alignItems: "stretch",
        }}
      >
        {/* LEFT — personal + documents */}
        <div>
          <Card title="Personal Information" icon="👤" style={{ height: "100%", marginBottom: 0 }}>
            <InfoRow label="Full Name" value={`${applicantData.firstName || ""} ${applicantData.lastName || ""}`.trim() || "—"} />
            <InfoRow label="Email" value={applicantData.email || "—"} />
            <InfoRow label="Gender" value={applicantData.gender || "—"} />
            <InfoRow
              label="Applicant Type"
              value={applicantData.source === 1 ? "Internal" : "External"}
            />
            {applicantData.forJobApply && (
              <InfoRow label="Applied For" value={applicantData.forJobApply} />
            )}
            {applicantData.createdOn && (
              <InfoRow
                label="Applied On"
                value={new Date(applicantData.createdOn).toLocaleDateString()}
              />
            )}
            {applicantData.workAuthorizationStatus != null && (
              <InfoRow
                label="Work Authorization"
                value={
                  WorkAuthorizationStatusEnumLabel[applicantData.workAuthorizationStatus] || "—"
                }
              />
            )}
            {typeof applicantData.sponsorshipNeeded === "boolean" && (
              <InfoRow
                label="Sponsorship Needed"
                value={applicantData.sponsorshipNeeded ? "Yes" : "No"}
              />
            )}
          </Card>
        </div>

        {/* RIGHT — job posting overview */}
        <div>
          <Card title="Job Posting Overview" icon="💼" style={{ height: "100%", marginBottom: 0 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "12px 24px",
              }}
            >
              <InfoRow label="Job Title" value={job.jobTitle || "—"} />
              <InfoRow label="Department" value={job.departmentName || "—"} />
              <InfoRow label="Location Type" value={job.jobLocationType || "—"} />
              <InfoRow label="Job Type" value={job.jobType || "—"} />
              {job.employmentType != null && job.employmentType !== "" && (
                <InfoRow
                  label="Employment Type"
                  value={EmploymentTypeEnumLabel[job.employmentType] || job.employmentType}
                />
              )}
              {job.compensation && (
                <InfoRow label="Compensation" value={`${job.compensation} USD/Month`} />
              )}
              {job.maximumExperience && (
                <InfoRow label="Experience" value={job.maximumExperience} />
              )}
              {job.jobInternalCode && (
                <InfoRow label="Internal Code" value={job.jobInternalCode} />
              )}
              {job.createdOn && (
                <InfoRow
                  label="Posted On"
                  value={new Date(job.createdOn).toLocaleDateString()}
                />
              )}
            </div>

            {job.jobDescription && (
              <>
                <LabelMeta style={{ marginTop: "1.2rem" }}>📌 Description</LabelMeta>
                <div
                  style={{
                    marginTop: "8px",
                    background: "#f8fafd",
                    padding: "14px 16px",
                    borderRadius: "20px",
                    fontSize: "0.9rem",
                    lineHeight: 1.55,
                    color: theme.text,
                    maxHeight: "320px",
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: job.jobDescription }}
                />
              </>
            )}

            {getPostingSkills(job).length > 0 && (
              <>
                <LabelMeta style={{ marginTop: "1.2rem" }}>🛠️ Required Skills</LabelMeta>
                <PillRow items={getPostingSkills(job)} variant="skill" />
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

OverviewTab.propTypes = {
  applicantData: PropTypes.object,
};

// Current pipeline stage + a stepper of all stages (per Jun-20 punch-list:
// "candidate detail should show the current pipeline status of the application").
function ApplicationStatus({ status }) {
  const isReject = status === JobApplicationStatusEnum.Reject;
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <Card title="Application Status" icon="📌">
        <div style={{ marginBottom: 14 }}>
          <span
            style={{
              display: "inline-block",
              background: pipelineStatusColor(status),
              color: "#fff",
              padding: "5px 16px",
              borderRadius: "40px",
              fontSize: "0.9rem",
              fontWeight: 600,
            }}
          >
            {JobApplicationStatusLabel[status] || "Unknown"}
          </span>
          <div style={{ marginTop: 8, fontSize: "0.88rem", color: theme.textMuted }}>
            {JobApplicationStatusText[status] || ""}
          </div>
        </div>

        {isReject ? (
          <div
            style={{
              background: "#fff0ee",
              border: "1px solid #f5c2c2",
              borderRadius: "16px",
              padding: "12px 16px",
              color: "#7a1f1f",
              fontSize: "0.88rem",
              fontWeight: 500,
            }}
          >
            🚫 This application was rejected and is no longer in the pipeline.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {PIPELINE.map((s) => {
              const done = status > s;
              const current = status === s;
              const bg = current ? "#0f5a2a" : done ? "#e6f7ec" : "#f0f2f5";
              const color = current ? "#fff" : done ? "#0f5a2a" : "#9aa3ad";
              return (
                <span
                  key={s}
                  style={{
                    padding: "5px 12px",
                    borderRadius: "40px",
                    fontSize: "0.78rem",
                    fontWeight: current ? 700 : 500,
                    background: bg,
                    color,
                    border: current ? "1px solid #0f5a2a" : "1px solid transparent",
                  }}
                >
                  {done ? "✓ " : ""}
                  {JobApplicationStatusLabel[s]}
                </span>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
ApplicationStatus.propTypes = { status: PropTypes.number };

function InfoRow({ label, value }) {
  return (
    <div style={{ marginBottom: "10px" }}>
      <div
        style={{
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: theme.textLabel,
          fontWeight: 600,
          marginBottom: "2px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "0.95rem", color: theme.text, fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}
InfoRow.propTypes = { label: PropTypes.string, value: PropTypes.node };

export default OverviewTab;
